import { AccountFeatureEnum, AccountTypeEnum, UserPermissions } from '@anecdotes/api-contracts';
import { AuthService, RoleEnum, UserClaims, PermissionsApiService } from '@anecdotes/auth-core';
import { FeatureFlagsService } from '@anecdotes/feature-flags';
import { FeaturesService } from '@anecdotes/feature-management';
import { Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { combineLatest, firstValueFrom, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { CustomerFacadeService } from '@anecdotes/data-store';

@Injectable()
export class FeatureFlagsEffects {
  constructor(
    private authService: AuthService,
    private featureFlagsService: FeatureFlagsService,
    private customerFacade: CustomerFacadeService,
    private featuresService: FeaturesService,
    private permissionsApi: PermissionsApiService,
  ) {}

  loadPermissions$ = createEffect(
    () =>
      this.authService.getUser().pipe(
        filter(Boolean),
        take(1),
        switchMap((userClaims) => this.getPermissionsAsync(userClaims)),
        tap((permissionsObject) => this.featureFlagsService.setPermissions(permissionsObject.permissions)),
        switchMap((permissionsObject) => {
          const hasCustomerReadPermission = permissionsObject.permissions.includes('customer:read') && !permissionsObject.roles.includes(RoleEnum.Zone);

          const customerFeaturesStream = hasCustomerReadPermission
            ? this.customerFacade.getCurrentCustomerAccountFeatures()
            : of([]);

          const customerAccountTypeStream = hasCustomerReadPermission
            ? this.customerFacade.getCurrentCustomerAccountType()
            : of(null);

          return combineLatest([
            this.featuresService
              .getEntitlements()
              .pipe(
                map((features) => features.filter((feature) => feature.hasAccess).map((feature) => feature.featureId)),
              ),
            customerFeaturesStream,
            customerAccountTypeStream,
          ]).pipe(
            tap(([enabledFeatureIds, accountFeatures, accountType]) => {              
              this.featureFlagsService.setPackageFeatures([
                ...enabledFeatureIds, 
                ...(accountFeatures || []),
                ...(accountType !== AccountTypeEnum.DataEngine ? [AccountFeatureEnum.NotDataEngine] : [])
              ]);
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  async getPermissionsAsync(currentUserClaims: UserClaims): Promise<UserPermissions> {
    // TODO: To remove getting user claims when we get rid of old role based approach from everywhere
    const currentRole = currentUserClaims.role.split(':')[1];
    const permissionsObject = await firstValueFrom(this.permissionsApi.getPermissions());
    const result = [...permissionsObject.permissions, ...permissionsObject.roles, currentRole];

    // TODO: This is special handling of Manager for backward compatibility with all features that still use Collaborator
    if ([RoleEnum.Collaborator, RoleEnum.Manager].includes(currentRole as RoleEnum)) {
      result.push(RoleEnum.Manager, RoleEnum.Collaborator);
    }

    // TODO: To remove getting user claims when we get rid of old role based approach from everywhere
    if (currentUserClaims.is_custom_role) {
      result.push('customRole');
    }

    if (permissionsObject.resource_permissions) {
      result.push(...permissionsObject.resource_permissions);
    }

    return {
      permissions: result,
      roles: permissionsObject.roles,
    };
  }
}
